/**
 * One way to turn notifications on for THIS device, used by every place that
 * asks (the value-first prompt, the Dashboard "coach can't reach you" card,
 * Settings). iOS app → APNs via the native plugin; web and the Android app →
 * web push. Never shows a browser prompt unless the user just tapped a button.
 */
import { isCapacitorNative, nativePushAvailable, registerNativePush, checkNativePushPermission } from './capacitor';
import { registerWebPushSubscription, webPushSupported, urlBase64ToUint8Array } from './webPush';
import { isTWA } from './platform';
import { API_BASE_URL } from '../services/api';

export type EnablePushResult = 'granted' | 'denied' | 'unsupported' | 'failed';

export async function enablePushOnThisDevice(): Promise<EnablePushResult> {
  if (isCapacitorNative()) {
    // An Android app built without Firebase cannot receive pushes (its web view has no web push).
    if (!nativePushAvailable()) return 'unsupported';
    try {
      const ok = await registerNativePush(API_BASE_URL);
      if (ok) { markPushDevicesDirty(); return 'granted'; }
      const status = await checkNativePushPermission();
      return status === 'denied' ? 'denied' : 'failed';
    } catch { return 'failed'; }
  }
  if (!webPushSupported()) return 'unsupported';
  try {
    let permission = Notification.permission;
    if (permission !== 'granted') permission = await Notification.requestPermission();
    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      // The Android app's browser layer can answer "denied" or "default" while
      // the app-level permission is granted; subscribing is the real test there.
      if (permission !== 'granted' && !isTWA()) return 'denied';
      try {
        sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(process.env.REACT_APP_VAPID_PUBLIC_KEY || '') });
      } catch {
        return permission === 'granted' ? 'failed' : 'denied';
      }
    }
    const ok = await registerWebPushSubscription(API_BASE_URL, sub);
    if (ok) {
      localStorage.setItem('fueltron_webpush_registered_at', String(Date.now()));
      markPushDevicesDirty();
    }
    return ok ? 'granted' : 'failed';
  } catch {
    return 'failed';
  }
}

const DEVICES_KEY = 'ft_push_devices';
const DEVICES_TTL_MS = 10 * 60 * 1000;

/** How many devices the account can be pushed to (null when unknown). Cached 10 min per tab. */
export async function accountPushDeviceCount(): Promise<number | null> {
  try {
    const raw = sessionStorage.getItem(DEVICES_KEY);
    if (raw) {
      const { n, at } = JSON.parse(raw);
      if (Date.now() - at < DEVICES_TTL_MS && typeof n === 'number') return n;
    }
  } catch { /* fall through */ }
  const token = localStorage.getItem('token');
  if (!token) return null;
  try {
    const res = await fetch(`${API_BASE_URL}/notifications/devices`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return null;
    const n = ((await res.json()).data?.devices || []).length as number;
    try { sessionStorage.setItem(DEVICES_KEY, JSON.stringify({ n, at: Date.now() })); } catch { /* private mode */ }
    return n;
  } catch {
    return null;
  }
}

export function markPushDevicesDirty(): void {
  try { sessionStorage.removeItem(DEVICES_KEY); } catch { /* ignore */ }
}
